import { config } from "../../config";

interface BrandmarkProps {
  size?: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * OrbitCell brand mark.
 *
 * Composition:
 *   - dark rounded card (the "control plane" container)
 *   - isometric cube wireframe in white stroke (the orbit, the boundary)
 *   - 8-tooth gear nested in the lower-front face (the cell, the running
 *     thing inside)
 *   - one green node at the gear core (status / live signal)
 *
 * Industrial-serious, monochrome with a single signal accent. The cube
 * reads at favicon scale even when the gear teeth blur. The brand mark
 * is intentionally generic in form so the same SVG ships under any
 * brand name -- aria-label is interpolated from runtime config.
 *
 * Keep this inline SVG in sync with landing_frontend/brand/mark/mark.svg,
 * the source brand mark used for favicons and generated app icons.
 */
export default function Brandmark({
  size = 28,
  className,
  ariaLabel,
}: BrandmarkProps) {
  const label = ariaLabel ?? config.brandName;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label={label}
      className={className}
    >
      <defs>
        <linearGradient id="oc-bg" x1="6" y1="6" x2="58" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#18181b" />
          <stop offset="1" stopColor="#09090b" />
        </linearGradient>
        <linearGradient
          id="oc-stroke"
          x1="14"
          y1="10"
          x2="50"
          y2="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#fafafa" />
          <stop offset="1" stopColor="#a1a1aa" />
        </linearGradient>
      </defs>

      {/* Card */}
      <rect x="4" y="4" width="56" height="56" rx="12" fill="url(#oc-bg)" />
      <rect
        x="4.5"
        y="4.5"
        width="55"
        height="55"
        rx="11.5"
        fill="none"
        stroke="#fafafa"
        strokeOpacity="0.08"
      />

      {/* Isometric cube wireframe -- top face + 2 front faces */}
      <g
        stroke="url(#oc-stroke)"
        strokeWidth="1.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.92"
      >
        {/* Top */}
        <path d="M 32 10 L 50 20 L 32 30 L 14 20 Z" />
        {/* Front-left */}
        <path d="M 14 20 L 32 30 L 32 50 L 14 40 Z" />
        {/* Front-right */}
        <path d="M 50 20 L 32 30 L 32 50 L 50 40 Z" />
      </g>

      {/* Gear inside the front of the cube */}
      <g transform="translate(32 38)">
        {/* 8 teeth */}
        <g fill="#fafafa">
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(45)" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(90)" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(135)" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(180)" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(225)" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(270)" />
          <rect x="-0.9" y="-9.2" width="1.8" height="3.2" rx="0.5" transform="rotate(315)" />
        </g>
        {/* Gear body */}
        <circle r="5" fill="#0f0f12" stroke="#fafafa" strokeWidth="1.2" />
        {/* Status node */}
        <circle r="1.7" fill="#22c55e" />
      </g>
    </svg>
  );
}
